import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { useFilterValueCounts } from '../useFilterValueCounts'
import type { ValuePickerProps } from './types'
import { useProjects } from '@/lib/db/rxdb/hooks'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { IssueCommandInput } from '@/components/app/combo-box/IssueCommandInput'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { groupProjectsBySpace } from '@/lib/utils/groupProjectsBySpace'
import { cn } from '@/lib/utils'

export function ProjectFilterPicker({
  selectedValues,
  onToggleValue,
  allowedProjectIds,
}: ValuePickerProps) {
  const { t } = useTranslation('tasks')
  const { t: tProjects } = useTranslation('projects')
  const { projects } = useProjects({ isDeleted: false })
  const counts = useFilterValueCounts('project')

  const grouped = useMemo(() => {
    const allowedSet = allowedProjectIds ? new Set(allowedProjectIds) : null
    const selectableProjects = allowedSet
      ? projects.filter((p) => !p.parentProjectId || allowedSet.has(p.id))
      : projects

    return groupProjectsBySpace(selectableProjects)
  }, [allowedProjectIds, projects])

  return (
    <Command>
      <IssueCommandInput placeholder={t('filterBar.projectPlaceholder')} />
      <CommandList>
        <CommandEmpty>{t('filterBar.noResults')}</CommandEmpty>
        {grouped.map(({ space, children }) => {
          const headingLabel =
            space?.name ||
            (space
              ? tProjects('sidebar.doc.untitled')
              : t('filterBar.otherGroup'))
          const groupKey = space?.id ?? '__orphan__'
          return (
            <CommandGroup
              key={groupKey}
              heading={
                <span className="flex items-center gap-1.5">
                  {space && (
                    <ProjectIcon
                      iconType={space.iconType}
                      icon={space.icon}
                      iconColor={space.iconColor}
                      size={14}
                      className="text-muted-foreground"
                    />
                  )}
                  {headingLabel}
                </span>
              }
            >
              {children.map((project) => {
                const isSelected = selectedValues.includes(project.id)
                const projectName =
                  project.name || t('filterBar.untitledProject')
                return (
                  <CommandItem
                    key={project.id}
                    value={`${headingLabel} ${projectName}`}
                    onSelect={() => onToggleValue(project.id)}
                    className="flex items-center gap-2 text-xs pl-5"
                  >
                    <span className="flex items-center gap-2 flex-1">
                      <ProjectIcon
                        iconType={project.iconType}
                        icon={project.icon}
                        iconColor={project.iconColor}
                        size={14}
                        className="text-muted-foreground"
                      />
                      {projectName}
                    </span>
                    {counts && (
                      <span className="text-[11px] tabular-nums text-muted-foreground">
                        {counts.get(project.id) ?? 0}
                      </span>
                    )}
                    <Check
                      className={cn('size-3.5 shrink-0 text-primary', !isSelected && 'invisible')}
                    />
                  </CommandItem>
                )
              })}
            </CommandGroup>
          )
        })}
      </CommandList>
    </Command>
  )
}
